import {
  ACCESS_TOKEN,
  LOGIN_ROUTE,
  REFRESH_ROUTE,
  REFRESH_TOKEN,
  TOKENS_EXPIRY_ERROR,
  TOKENS_INVALID_ERROR,
} from '@pequity/shared/types/constants';
import { isOidcSession } from '@pequity/shared/utils/oidc';
import { setAccessToken, tokenExpired } from '@pequity/shared/utils/token';
import { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { debounce } from 'lodash-es';
import { refreshOidcToken } from '@/api/auth';
import { baseControllers } from '@/axios/controllers';
import { useAuthStore } from '@/stores/auth';

const logout = debounce(
  async () => {
    // Abort all pending requests
    for (const controller of Object.values(baseControllers)) {
      try {
        if (controller) {
          controller.abort();
        }
      } catch {
        // noop
      }
    }

    const authStore = useAuthStore();
    const redirectPath = authStore.path;

    authStore.logout();

    if (redirectPath && redirectPath !== '/') {
      const urlParams = new URLSearchParams({ redirect: authStore.path });
      window.location.href = `/v3/welcome-back/?${urlParams.toString()}`;
    } else {
      window.location.href = '/v3/welcome-back/';
    }
  },
  1500,
  {
    leading: true,
    trailing: false,
  }
);

let pendingRefresh: Promise<string> | null = null;

const doRefresh = async (): Promise<string> => {
  if (isOidcSession()) {
    const { data, error } = await refreshOidcToken();
    if (error || !data?.access_token) {
      throw new Error('OIDC token refresh failed');
    }
    setAccessToken(data.access_token);
    return data.access_token;
  }
  const authStore = useAuthStore();
  return authStore.refreshToken();
};

const refreshToken = async (): Promise<string> => {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = doRefresh().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
};

const isPequityRequest = (url: string) => url.includes(window.location.hostname);
const isRefreshRequest = (url: string) => url.includes(REFRESH_ROUTE) || url.includes('oidc/aim-refresh');
const isPublicAuthRequest = (url: string) =>
  url.includes(LOGIN_ROUTE) || url.includes('public-login') || url.includes('public-mfa-verification');

const getValidToken = async (): Promise<string | null> => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return null;
  if (!tokenExpired(token)) return token;

  if (!isOidcSession() && tokenExpired(localStorage.getItem(REFRESH_TOKEN) ?? '')) {
    throw Error(TOKENS_EXPIRY_ERROR);
  }

  try {
    await refreshToken();
    return localStorage.getItem(ACCESS_TOKEN);
  } catch {
    throw Error(TOKENS_INVALID_ERROR);
  }
};

export const useTokenInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    async (config) => {
      const url = new URL(config.url ?? '', config.baseURL).href;

      if (isPequityRequest(url)) {
        baseControllers[url] = new AbortController();
        config.signal = baseControllers[url].signal;

        if (isRefreshRequest(url) && isOidcSession()) {
          const token = localStorage.getItem(ACCESS_TOKEN);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else if (!isRefreshRequest(url)) {
          const token = await getValidToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const config = error?.config;

      if (error?.message === TOKENS_EXPIRY_ERROR || error?.message === TOKENS_INVALID_ERROR) {
        logout();
        return Promise.reject(error);
      }

      let url;
      try {
        url = new URL(config?.url ?? '', config?.baseURL).href;
      } catch (err) {
        url = '';
      }

      if (isPequityRequest(url) && error?.response?.status === 401) {
        if (isPublicAuthRequest(url)) {
          return Promise.reject(error);
        }
        if (url.includes(REFRESH_ROUTE) || url.includes('oidc/aim-refresh')) {
          logout();
          return Promise.reject(error);
        }
        if (!config || ('_retry' in config && config._retry)) {
          logout();
          return Promise.reject(error);
        }
        try {
          Object.assign(config, { _retry: true });
          await refreshToken();
          return instance(config);
        } catch {
          logout();
        }
      }

      return Promise.reject(error);
    }
  );
};
